import { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';
import { useVerifyMfaMutation, useLogoutMutation } from '@/app/services/authApi';
import { useNotification } from '@/hooks/useNotification';
import { getErrorMessage } from '@/utils/errorUtils';
import { osirisTokens } from '@/theme/tokens';
import {
  AuthHeader,
  AuthCodeInput,
  AuthPrimaryButton,
  AuthBottomNote,
  PURPLE_FILL,
} from './AuthPrimitives';

// TOTP codes refresh every 30s; reset countdown to the next multiple-of-30 boundary
const TOTP_PERIOD = 30;

function useTotpCountdown() {
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const now = Math.floor(Date.now() / 1000);
    return TOTP_PERIOD - (now % TOTP_PERIOD);
  });

  useEffect(() => {
    const id = window.setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      setSecondsLeft(TOTP_PERIOD - (now % TOTP_PERIOD));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return secondsLeft;
}

export function MfaVerificationForm() {
  const [verifyMfaMutation, { isLoading }] = useVerifyMfaMutation();
  const [logoutMutation] = useLogoutMutation();
  const { showErrorNotification } = useNotification();

  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState(false);

  const secondsLeft = useTotpCountdown();
  const progressPct = (secondsLeft / TOTP_PERIOD) * 100;

  const submitCode = (code: string) => {
    setError(false);
    verifyMfaMutation({ code })
      .unwrap()
      .catch((err) => {
        setError(true);
        const errorMessage =
          getErrorMessage(err as FetchBaseQueryError | SerializedError) ||
          'Invalid authentication code';
        showErrorNotification(errorMessage);
      });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length !== 6) {
      setError(true);
      return;
    }
    submitCode(code);
  };

  const handleSignOut = () => {
    logoutMutation();
  };

  return (
    <Box component="form" onSubmit={onSubmit} className="auth-mfa-form">
      <AuthHeader
        title="Two-Factor Verification"
        subtitle={
          <>
            Enter the 6-digit code from your authenticator app.
            <br />
            Code refreshes every 30 seconds.
          </>
        }
      />

      <Box sx={{ mb: 6 }}>
        <AuthCodeInput
          value={digits}
          onChange={(next) => {
            setDigits(next);
            setError(false);
          }}
          onComplete={submitCode}
          error={error}
        />
      </Box>

      <Box
        className="auth-mfa-countdown"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 6 }}
      >
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            flex: 1,
            height: 4,
            bgcolor: osirisTokens.alpha.dark200,
            '& .MuiLinearProgress-bar': {
              bgcolor: PURPLE_FILL,
            },
          }}
        />
        <Typography
          sx={{
            fontFamily: osirisTokens.fonts.body,
            fontSize: 14,
            color: osirisTokens.text.muted,
            minWidth: 32,
            textAlign: 'right',
          }}
        >
          {secondsLeft} s
        </Typography>
      </Box>

      <AuthPrimaryButton isLoading={isLoading} className="mfa-verify-submit">
        Verify
      </AuthPrimaryButton>

      <AuthBottomNote>
        Not you?{' '}
        <button type="button" onClick={handleSignOut} className="mfa-signout-link">
          Sign in with a different account
        </button>
      </AuthBottomNote>
    </Box>
  );
}
